import { forwardRef, useCallback, useRef, useState } from 'react';
import { Modal as AntdModal } from 'antd';
import { FocusTrap } from 'focus-trap-react';
import Draggable from 'react-draggable';
import { MenuDotsBoldIcon, CloseCircleBoldIcon } from '@solar-icons/react';
import clsx from 'clsx';
import Button from '../Button';
import styles from './Dialog.module.css';

const RefTrapTarget = forwardRef(({ innerRef, ...props }, _ref) => <AntdModal ref={innerRef} {...props} />);

const NUDGE_STEP_PX = 10;
const DRAG_HANDLE_SELECTOR = '[data-vec-dialog-drag-handle]';
const NUDGE_KEYS = {
    ArrowUp: { x: 0, y: -NUDGE_STEP_PX },
    ArrowDown: { x: 0, y: NUDGE_STEP_PX },
    ArrowLeft: { x: -NUDGE_STEP_PX, y: 0 },
    ArrowRight: { x: NUDGE_STEP_PX, y: 0 },
};

/**
 * @typedef {Object} DialogButton
 * @property {string} name - Button label text
 * @property {function(): (void|Promise<void>)} [callback] - Called on click; `onCancel` fires
 *   (closing the dialog) if omitted
 * @property {boolean} [disabled] - Disables the button
 * @property {boolean} [hidden] - Hides the button entirely
 * @property {Object} [extraProps] - Extra props forwarded to the Button component
 */

/**
 * @param {Object} props
 * @param {React.ReactNode} props.title - Dialog title (required — every Dialog needs one)
 * @param {React.ReactNode} [props.icon] - Icon rendered before the title text
 * @param {number|string} [props.width=520] - Preferred width; shrinks automatically on small screens
 * @param {boolean} [props.draggable=false] - Lets the dialog be dragged by its title bar, by mouse
 *   or (once focused) arrow keys
 * @param {function(node: React.ReactNode): React.ReactNode} [props.modalRender] - Composed with
 *   the drag wrapper when `draggable` is true, rather than being replaced by it
 * @param {DialogButton[]} [props.buttons] - Footer action buttons; replaces antd's own `footer`
 *   while present. A button with no `callback` calls `onCancel` instead. While one button's
 *   callback is pending, that button shows a spinner and every other button is disabled.
 */
const Dialog = forwardRef((
    {
        className, classNames, mask, closeIcon, title, icon, draggable = false, modalRender,
        buttons, footer, onCancel, open, afterOpenChange, ...props
    },
    ref
) => {
    const [panelElement, setPanelElement] = useState(null);
    const [trapActive, setTrapActive] = useState(false);
    const draggleRef = useRef(null);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [bounds, setBounds] = useState({ left: 0, top: 0, right: 0, bottom: 0 });
    const [processingIndex, setProcessingIndex] = useState(null);
    const isProcessing = processingIndex !== null;

    const handleButtonClick = useCallback(async (button, index) => {
        if (isProcessing) return;

        if (typeof button.callback !== 'function') {
            onCancel?.();
            return;
        }

        setProcessingIndex(index);
        try {
            await button.callback();
        } catch (error) {
            console.error('Error', error);
        } finally {
            setProcessingIndex(null);
        }
    }, [isProcessing, onCancel]);

    // Constrains dragging so the dialog can't be thrown fully off-screen — recomputed from the
    // dialog's current position each time a drag/nudge starts, per antd's own draggable Modal demo.
    const computeBounds = useCallback(currentPosition => {
        const { clientWidth, clientHeight } = window.document.documentElement;
        const targetRect = draggleRef.current?.getBoundingClientRect();
        if (!targetRect) return { left: 0, top: 0, right: 0, bottom: 0 };
        return {
            left: -targetRect.left + currentPosition.x,
            right: clientWidth - (targetRect.right - currentPosition.x),
            top: -targetRect.top + currentPosition.y,
            bottom: clientHeight - (targetRect.bottom - currentPosition.y),
        };
    }, []);

    const onDragStart = useCallback((_event, uiData) => {
        setBounds(computeBounds({ x: uiData.x, y: uiData.y }));
    }, [computeBounds]);

    const onDrag = useCallback((_event, uiData) => {
        setPosition({ x: uiData.x, y: uiData.y });
    }, []);

    // Same handle the mouse drags from, so arrow keys work as soon as it's focused — no separate
    // control to tab to.
    const onHandleKeyDown = useCallback(event => {
        const delta = NUDGE_KEYS[event.key];
        if (!delta) return;
        event.preventDefault();
        setPosition(prev => {
            const currentBounds = computeBounds(prev);
            return {
                x: Math.min(Math.max(prev.x + delta.x, currentBounds.left), currentBounds.right),
                y: Math.min(Math.max(prev.y + delta.y, currentBounds.top), currentBounds.bottom),
            };
        });
    }, [computeBounds]);

    const titleContent = icon ? <span className={styles.vecDialogTitle}>{icon}{title}</span> : title;

    const builtFooter = buttons?.filter(button => !button.hidden).map((button, index) => (
        <Button
            {...button.extraProps}
            key={index}
            disabled={button.disabled || (isProcessing && processingIndex !== index)}
            loading={processingIndex === index}
            onClick={() => handleButtonClick(button, index)}
        >
            {button.name}
        </Button>
    ));

    return (
        <FocusTrap
            active={Boolean(open) && trapActive}
            containerElements={panelElement ? [panelElement] : []}
        >
        <RefTrapTarget
            innerRef={ref}
            open={open}
            panelRef={setPanelElement}
            focusable={{ trap: false }}
            afterOpenChange={visible => {
                setTrapActive(visible);
                afterOpenChange?.(visible);
            }}
            className={clsx(styles.vecDialog, className)}
            classNames={{
                ...classNames,
                mask: clsx(styles.vecDialogMask, classNames?.mask),
            }}
            mask={{ blur: true }}
            closeIcon={<CloseCircleBoldIcon size={20} />}
            onCancel={onCancel}
            footer={buttons ? builtFooter : footer}
            title={draggable
                ? (
                    <div
                        className={styles.vecDialogDragHandle}
                        data-vec-dialog-drag-handle=""
                        tabIndex={0}
                        onKeyDown={onHandleKeyDown}
                    >
                        <span className={styles.vecDialogDragHandleTitle}>
                            {titleContent}
                            <span className={styles.vecSrOnly}> (draggable)</span>
                        </span>
                        <MenuDotsBoldIcon
                            size={18}
                            aria-hidden="true"
                            className={styles.vecDialogDragHandleIcon}
                        />
                    </div>
                )
                : titleContent}
            modalRender={draggable
                ? node => (
                    <Draggable
                        handle={DRAG_HANDLE_SELECTOR}
                        bounds={bounds}
                        position={position}
                        nodeRef={draggleRef}
                        onStart={onDragStart}
                        onDrag={onDrag}
                    >
                        <div ref={draggleRef}>{modalRender ? modalRender(node) : node}</div>
                    </Draggable>
                )
                : modalRender}
            {...props}
        />
        </FocusTrap>
    );
});
Dialog.displayName = 'Dialog';

export default Dialog;
